import { IColumnPreferencesRepository } from "../../adapters/driven/repositories/columnPreferencesRepository";

export interface IColumnPreferencesResult {
  viewKey: string;
  selectedColumns: string[];
  updatedAt: string;
}

// APIProvider.GET swallows HTTP errors and returns the response body as data,
// so we detect a 404 by inspecting the returned payload shape rather than
// catching a thrown exception.
function isNotFoundResponse(data: unknown): boolean {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  // { detail: { message: "No column preference saved for view '...'" } }
  if (d.detail && typeof d.detail === "object") {
    return true;
  }
  return false;
}

export const ColumnPreferencesServiceImplFactory = ({
  columnPreferencesRepository,
}: {
  columnPreferencesRepository: IColumnPreferencesRepository;
}) => {
  const getColumnPreferences = async (
    viewKey: string
  ): Promise<
    IControllerPromiseResponse<IColumnPreferencesResult, { message: string }>
  > => {
    try {
      const data = await columnPreferencesRepository.getColumnPreferences(
        viewKey
      );

      // No preference saved yet — caller should fall back to the frontend default.
      if (isNotFoundResponse(data)) {
        return { success: false, error: { message: "NOT_FOUND" } };
      }

      return {
        success: true,
        data: {
          viewKey: data.view_key,
          selectedColumns: data.selected_columns,
          updatedAt: data.updated_at,
        },
      };
    } catch (error: unknown) {
      console.error(
        "[ColumnPreferencesService] getColumnPreferences error:",
        error
      );
      return {
        success: false,
        error: { message: "Failed to fetch column preferences" },
      };
    }
  };

  const saveColumnPreferences = async (
    viewKey: string,
    selectedColumns: string[]
  ): Promise<
    IControllerPromiseResponse<IColumnPreferencesResult, { message: string }>
  > => {
    try {
      const data = await columnPreferencesRepository.putColumnPreferences({
        view_key: viewKey,
        selected_columns: selectedColumns,
      });
      return {
        success: true,
        data: {
          viewKey: data.view_key,
          selectedColumns: data.selected_columns,
          updatedAt: data.updated_at,
        },
      };
    } catch (error: unknown) {
      console.error(
        "[ColumnPreferencesService] saveColumnPreferences error:",
        error
      );
      return {
        success: false,
        error: { message: "Failed to save column preferences" },
      };
    }
  };

  return {
    getColumnPreferences,
    saveColumnPreferences,
  };
};
